import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    const checkAuth = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        // If returning from Emergent OAuth, AuthCallback will handle it
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        checkAuth();
    }, [checkAuth]);

    // Re-fetch /auth/me when the tab regains focus so role/permission changes
    // pushed by an admin (e.g., updated board permissions) flow through without
    // forcing the user to sign out.
    useEffect(() => {
        const onFocus = () => {
            if (document.visibilityState === "visible") {
                api.get("/auth/me").then(({ data }) => setUser((curr) => (curr ? data : curr))).catch(() => {});
            }
        };
        window.addEventListener("focus", onFocus);
        document.addEventListener("visibilitychange", onFocus);
        return () => {
            window.removeEventListener("focus", onFocus);
            document.removeEventListener("visibilitychange", onFocus);
        };
    }, []);

    const loginAdmin = async (email, password) => {
        const { data } = await api.post("/auth/admin/login", { email, password });
        // If 2FA required, do NOT setUser yet — caller drives the WebAuthn ceremony.
        if (data && !data.requires_2fa) setUser(data);
        return data;
    };

    const loginUser = async (email, password) => {
        const { data } = await api.post("/auth/login", { email, password });
        if (data && !data.requires_2fa) setUser(data);
        return data;
    };

    const completeWebAuthn = (data) => {
        setUser(data);
    };

    const registerUser = async (payload) => {
        // payload = { email, name, street, city, state, zip_code, password? }
        // When password is omitted, backend emails a magic link and returns
        // { ok:true, email_sent:true, message } WITHOUT signing in.
        const { data } = await api.post("/auth/register", payload);
        if (data && data.access_token && !data.email_sent) setUser(data);
        return data;
    };

    const requestMagicLink = async (email) => {
        const { data } = await api.post("/auth/magic-link/request", { email });
        return data;
    };

    const claimMagicLink = async (token, newPassword) => {
        const { data } = await api.post("/auth/magic-link/claim", {
            token,
            new_password: newPassword || undefined,
        });
        if (data && !data.requires_2fa) setUser(data);
        return data;
    };

    const loginFacebookMock = async () => {
        const { data } = await api.post("/auth/facebook/login", { mock_user: null });
        setUser(data);
        return data;
    };

    const exchangeGoogleSession = async (sessionId) => {
        const { data } = await api.post("/auth/google/session", { session_id: sessionId });
        setUser(data);
        return data;
    };

    const loginGoogleOAuth = async (code) => {
        // Custom Google OAuth (admin-provisioned). The frontend uses
        // @react-oauth/google's auth-code flow which returns a code + uses
        // 'postmessage' as the redirect_uri.
        const { data } = await api.post("/auth/google", { code, redirect_uri: "postmessage" });
        setUser(data.user || null);
        return data;
    };

    const loginFacebookOAuth = async (accessToken) => {
        // Custom Facebook Login (admin-provisioned). Frontend uses the official
        // FB JS SDK to obtain an access token; backend verifies + upserts user.
        const { data } = await api.post("/auth/facebook", { access_token: accessToken });
        setUser(data.user || null);
        return data;
    };

    const logout = async () => {
        try { await api.post("/auth/logout"); } catch {}
        setUser(null);
    };

    return (
        <AuthContext.Provider
            value={{ user, loading, loginAdmin, loginUser, registerUser, requestMagicLink, claimMagicLink, completeWebAuthn, loginFacebookMock, exchangeGoogleSession, loginGoogleOAuth, loginFacebookOAuth, logout, setUser }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
    return ctx;
};
